import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Map as MapIcon } from 'lucide-react';
import {
  adminAreaAt,
  applyPlacement,
  createSite,
  fetchPlacementReview,
  saveSiteArea,
  unassignPlacement,
} from '../../api/client';
import type { Me } from '../../api/client';
import { Modal } from '../../ui/components';
import { ErrorNote } from '../admin/ErrorNote';
import type { MapHighlight } from '../map/highlight';
import './placementReview.css';

type Item = api.PlacementItem;
type Site = api.Site;

/** The platform-wide review and the manual decisions are the superadmin's: global device.provision + site.manage. */
export const decidesPlacement = (me: Me | null | undefined) =>
  ['device.provision', 'site.manage'].every((perm) =>
    Boolean(me?.grants.some((g) => g.permission === perm && g.site_id === null)),
  );

export const placementKey = (siteId: string | null) =>
  ['admin', 'placement-review', siteId ?? 'all'] as const;

interface Group {
  key: string;
  current: Item['current'];
  reason: Item['reason'];
  target: Item['target'];
  items: Item[];
}

/** Devices with the same current site, reason and proposed target form one group (one checkbox for all of them). */
export function groupItems(items: readonly Item[]): Group[] {
  const groups = new Map<string, Group>();
  for (const i of items) {
    const key = `${i.current.site_id}|${i.reason}|${i.target?.site_id ?? ''}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { key, current: i.current, reason: i.reason, target: i.target, items: [] }));
    g.items.push(i);
  }
  return [...groups.values()].sort(
    (a, b) =>
      Number(b.items.some((i) => i.movable)) - Number(a.items.some((i) => i.movable)) ||
      b.items.length - a.items.length,
  );
}

type Result =
  | { kind: 'moved'; moved: number; skipped: number }
  | { kind: 'unassigned'; unassigned: number; skipped: number }
  | { kind: 'client'; name: string };

/**
 * Device placement review (U17, user decisions 2026-10-08): devices whose position lies outside their site's area,
 * grouped by "now on → proposed site", with a checkbox per group and per device; nothing is selected when it opens.
 * GPS-adopted devices may move to the proposed site; a superadmin (`canDecide`) may also select imported or manually
 * adopted ones, move any selection to a site of their choice, send it back to Discovered, or create a client from the
 * OpenStreetMap commune at a group's position. Without `siteId` the whole platform is reviewed (superadmin).
 */
export function PlacementReview({
  siteId,
  sites,
  canDecide,
  onClose,
  returnFocus,
}: {
  siteId: string | null;
  sites: readonly Site[];
  canDecide: boolean;
  onClose: () => void;
  returnFocus?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const review = useQuery({ queryKey: placementKey(siteId), queryFn: () => fetchPlacementReview(siteId) });
  const items = useMemo(() => review.data?.items ?? [], [review.data]);
  const groups = useMemo(() => groupItems(items), [items]);
  const selectable = (i: Item) => canDecide || i.movable;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Nothing is selected when the review opens (user, 2026-10-08); after each reload, devices no longer listed leave
  // the selection.
  useEffect(() => {
    setSelected((cur) => new Set(items.filter((i) => cur.has(i.device_id)).map((i) => i.device_id)));
  }, [items]);
  const [result, setResult] = useState<Result | null>(null);
  const [targetSite, setTargetSite] = useState('');
  const [confirmUnassign, setConfirmUnassign] = useState(false);

  const chosen = items.filter((i) => selected.has(i.device_id));
  const toTarget = chosen.filter((i) => i.movable);
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'placement-review'] });

  const move = useMutation({
    mutationFn: (to: string | undefined) =>
      applyPlacement(
        (to ? chosen : toTarget).map((i) => i.device_id),
        to,
      ),
    onSuccess: async (r) => {
      setResult({ kind: 'moved', moved: r.moved.length, skipped: r.skipped.length });
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['admin', 'sites'] })]);
    },
  });
  const unassign = useMutation({
    mutationFn: () => unassignPlacement(chosen.map((i) => i.device_id)),
    onSuccess: async (r) => {
      setConfirmUnassign(false);
      setResult({ kind: 'unassigned', unassigned: r.unassigned.length, skipped: r.skipped.length });
      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['admin', 'sites'] })]);
    },
  });

  const toggle = (ids: string[], on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      for (const id of ids) {
        if (on) n.add(id);
        else n.delete(id);
      }
      return n;
    });

  const names = useMemo(() => sitePaths(sites), [sites]);
  const siteOptions = useMemo(
    () =>
      sites
        .filter((s) => s.status === 'active')
        .map((s) => ({ id: s.id, label: names.get(s.id) ?? s.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [sites, names],
  );
  const path = (s: Item['current'] | null) => s?.path.join(' › ') ?? '—';
  const navigate = useNavigate();
  // The map opens with the group's devices pointed out; its banner leads back here (the review reopens).
  const viewOnMap = (g: Group, label: string) => {
    const highlight: MapHighlight = {
      label,
      points: g.items.map((i) => ({ id: i.device_id, name: i.name, lon: i.lon, lat: i.lat })),
      returnTo: siteId
        ? { path: '/sites', state: { open: siteId, placementReview: siteId } }
        : { path: '/sites', state: { placementReview: 'all' } },
    };
    void navigate('/map', { state: { highlight } });
  };
  const busy = move.isPending || unassign.isPending;

  return (
    <Modal
      title={t(siteId ? 'placement.title' : 'placement.titleAll')}
      subtitle={t('placement.subtitle')}
      onClose={onClose}
      {...(returnFocus ? { returnFocus } : {})}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {review.isPending && <span className="muted">{t('common.loading')}</span>}
        <ErrorNote error={review.error ?? move.error ?? unassign.error} />
        {result && (
          <div className="notice" role="status">
            {result.kind === 'moved'
              ? t('placement.result', { moved: result.moved, skipped: result.skipped })
              : result.kind === 'unassigned'
                ? t('placement.unassignResult', { count: result.unassigned, skipped: result.skipped })
                : t('placement.clientCreated', { name: result.name })}
          </div>
        )}
        {review.isSuccess && !items.length && <span className="muted">{t('placement.none')}</span>}
        {groups.length > 0 && (
          <>
            <span className="small muted">
              {t(canDecide ? 'placement.hintSuperadmin' : 'placement.hint')}
            </span>
            <div className="table-scroll" style={{ maxHeight: '52vh' }}>
              <table className="table placement-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }} aria-label={t('placement.col.select')} />
                    <th>{t('placement.col.device')}</th>
                    <th>{t('placement.col.source')}</th>
                  </tr>
                </thead>
                {groups.map((g) => (
                  <GroupRows
                    key={g.key}
                    group={g}
                    selected={selected}
                    selectable={selectable}
                    onToggle={toggle}
                    path={path}
                    canDecide={canDecide}
                    onViewMap={viewOnMap}
                    onClientCreated={async (name) => {
                      setResult({ kind: 'client', name });
                      await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['admin', 'sites'] })]);
                    }}
                  />
                ))}
              </table>
            </div>
            <div className="toolbar" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              <span className="muted">{t('placement.selected', { count: chosen.length })}</span>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !toTarget.length}
                onClick={() => move.mutate(undefined)}
              >
                {t('placement.move', { count: toTarget.length })}
              </button>
              {canDecide && (
                <>
                  <select
                    className="input"
                    aria-label={t('placement.moveToSite')}
                    value={targetSite}
                    onChange={(e) => setTargetSite(e.target.value)}
                    style={{ maxWidth: 280 }}
                  >
                    <option value="">{t('placement.moveToSite')}</option>
                    {siteOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy || !targetSite || !chosen.length}
                    onClick={() => move.mutate(targetSite)}
                  >
                    {t('placement.moveHere', { count: chosen.length })}
                  </button>
                  {confirmUnassign ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={busy || !chosen.length}
                        onClick={() => unassign.mutate()}
                      >
                        {t('placement.unassignConfirm', { count: chosen.length })}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setConfirmUnassign(false)}
                      >
                        {t('common.cancel')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy || !chosen.length}
                      onClick={() => setConfirmUnassign(true)}
                    >
                      {t('placement.unassign', { count: chosen.length })}
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

const groupLabel = (g: Group, path: (s: Item['current'] | null) => string, reason: string) =>
  g.reason === 'other_area' ? `${path(g.current)} → ${path(g.target)}` : `${path(g.current)} · ${reason}`;

function GroupRows({
  group,
  selected,
  selectable,
  onToggle,
  path,
  canDecide,
  onClientCreated,
  onViewMap,
}: {
  group: Group;
  selected: ReadonlySet<string>;
  selectable: (i: Item) => boolean;
  onToggle: (ids: string[], on: boolean) => void;
  path: (s: Item['current'] | null) => string;
  canDecide: boolean;
  onClientCreated: (name: string) => Promise<void>;
  onViewMap: (group: Group, label: string) => void;
}) {
  const { t } = useTranslation();
  const ids = group.items.filter(selectable).map((i) => i.device_id);
  const on = ids.filter((id) => selected.has(id)).length;
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (box.current) box.current.indeterminate = on > 0 && on < ids.length;
  }, [on, ids.length]);
  const label = groupLabel(group, path, t(`placement.reason.${group.reason}`, { site: '' }));
  return (
    <tbody>
      <tr className="placement-group">
        <td>
          <input
            ref={box}
            type="checkbox"
            aria-label={t('placement.selectGroup', { group: label })}
            disabled={!ids.length}
            checked={ids.length > 0 && on === ids.length}
            onChange={(e) => onToggle(ids, e.target.checked)}
          />
        </td>
        <td colSpan={2}>
          <div className="placement-group-head">
            <strong>{label}</strong>
            <span className="muted">({group.items.length})</span>
            <span className="placement-group-actions">
              {canDecide && group.reason !== 'other_area' && (
                <CreateClient at={group.items[0]!} onCreated={onClientCreated} />
              )}
              <button
                type="button"
                className="btn btn-ghost"
                aria-label={t('placement.viewOnMapGroup', { group: label })}
                onClick={() => onViewMap(group, label)}
              >
                <MapIcon size={15} /> {t('placement.viewOnMap')}
              </button>
            </span>
          </div>
        </td>
      </tr>
      {group.items.map((i) => (
        <tr key={i.device_id} className="placement-item">
          <td>
            <input
              type="checkbox"
              aria-label={i.name}
              disabled={!selectable(i)}
              checked={selected.has(i.device_id)}
              onChange={(e) => onToggle([i.device_id], e.target.checked)}
            />
          </td>
          <td className="mono">{i.name}</td>
          <td>{t(`placement.source.${i.source}`)}</td>
        </tr>
      ))}
    </tbody>
  );
}

/**
 * Superadmin: a new client named after the OpenStreetMap commune at the group's position, with that commune's
 * boundary as its area. The group's devices then resolve to it and can be moved with the normal selection.
 */
function CreateClient({ at, onCreated }: { at: Item; onCreated: (name: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [name, setName] = useState<string | null>(null);
  const area = useMutation({ mutationFn: () => adminAreaAt(at.lon, at.lat) });
  const create = useMutation({
    mutationFn: async () => {
      const found = area.data?.area;
      if (!found || !name?.trim()) throw new Error('no area');
      const site = await createSite({
        parent_id: null,
        kind: 'organization',
        name: name.trim(),
      });
      await saveSiteArea(site.id, found.geometry);
      return site.name;
    },
    onSuccess: async (n) => {
      setName(null);
      area.reset();
      await onCreated(n);
    },
  });
  if (name === null)
    return area.isSuccess && !area.data.area ? (
      <span className="small muted">
        {t(area.data.enabled ? 'placement.noCommune' : 'placement.noNominatim')}
      </span>
    ) : (
      <button
        type="button"
        className="btn btn-ghost"
        disabled={area.isPending}
        onClick={() => area.mutate(undefined, { onSuccess: (r) => r.area && setName(r.area.name) })}
      >
        {area.isPending ? t('common.loading') : t('placement.createClient')}
      </button>
    );
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' }}>
      <input
        className="input"
        aria-label={t('placement.clientName')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ maxWidth: 200 }}
      />
      <button
        type="button"
        className="btn btn-primary"
        disabled={create.isPending || !name.trim()}
        onClick={() => create.mutate()}
      >
        {t('placement.createClientConfirm')}
      </button>
      <button type="button" className="btn btn-ghost" onClick={() => setName(null)}>
        {t('common.cancel')}
      </button>
      {area.data?.area && (
        <span className="small muted">{t('placement.clientArea', { name: area.data.area.name })}</span>
      )}
      <ErrorNote error={create.error} />
    </span>
  );
}

/** "Client › Locality › Zone" for every site, from the flat site list. */
function sitePaths(sites: readonly Site[]): Map<string, string> {
  const byId = new Map(sites.map((s) => [s.id, s]));
  const out = new Map<string, string>();
  const label = (s: Site): string => {
    const cached = out.get(s.id);
    if (cached) return cached;
    const parent = s.parent_id ? byId.get(s.parent_id) : undefined;
    const l = parent ? `${label(parent)} › ${s.name}` : s.name;
    out.set(s.id, l);
    return l;
  };
  sites.forEach(label);
  return out;
}
